'use client'

import { useEffect, useState } from 'react'

import {
  ChatTeardropIcon,
  CopyIcon,
  HeartIcon,
  InfoIcon,
  ShareFatIcon
} from '@phosphor-icons/react'
import { useToggle } from '@reactuses/core'
import { useTranslations } from 'next-intl'

import { Copy, ShareMenu } from '@/components/common'
import { Button } from '@/components/ui/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { LikeStatus } from '@/enums/const'
import { useCountFormat } from '@/hooks'
import { Link } from '@/i18n/navigation'
import { useLikeWork, useUnlikeWork } from '@/lib/api/hooks/social'
import { cn } from '@/lib/utils'

import { UserAvatar } from '../user-avatar'
import { useWorkContext } from '../work-context'
import { WorkDetails } from '../work-details'

export const UserSection = ({ className }: { className?: string }) => {
  const format = useCountFormat()
  const t = useTranslations()
  const { data } = useWorkContext()

  const [expanded, toggleExpanded] = useToggle(false)
  const [shareLink, setShareLink] = useState('')

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShareLink(window.location.href)
  }, [])

  const likeMutation = useLikeWork(data?.jobId || '')
  const unlikeMutation = useUnlikeWork(data?.jobId || '')

  const isLiked = data?.isLike === LikeStatus.LIKE
  const isPending = likeMutation.isPending || unlikeMutation.isPending

  const handleLike = () => {
    if (!data?.jobId || isPending) return

    if (isLiked) {
      unlikeMutation.mutate(data.jobId)
    } else {
      likeMutation.mutate(data.jobId)
    }
  }

  return (
    <div
      className={cn(
        'rounded-3xl border border-solid border-white/5 bg-quaternary px-5 py-6',
        className
      )}
    >
      <div className="flex flex-col gap-2">
        <UserAvatar />
        <Link href={`https://digen.ai/@${data?.nickName}`}>
          <span className="truncate text-sm font-bold">{data?.nickName}</span>
        </Link>
        {data?.extra?.videoGenPrompt && (
          <ScrollArea className="line-clamp-2 inline max-h-[30vh] overflow-y-auto">
            <span className={cn(expanded ? 'line-clamp-none' : 'line-clamp-2')}>
              {data.extra.videoGenPrompt}
            </span>
            <button
              className="ml-1 cursor-pointer text-light/40 hover:underline"
              type="button"
              onClick={() => toggleExpanded()}
            >
              {expanded ? 'less' : 'more'}
            </button>
            <Copy asChild text={data.extra.videoGenPrompt} type="prompt">
              <button
                className="ml-1 cursor-pointer align-middle text-light/40 hover:underline"
                type="button"
              >
                <CopyIcon />
              </button>
            </Copy>
          </ScrollArea>
        )}
      </div>
      <div className="mt-6 flex items-center gap-4">
        <Button className="gap-1 rounded-full" variant="ghost" onClick={handleLike}>
          <HeartIcon size={18} strokeWidth={1.6} weight={isLiked ? 'fill' : 'regular'} />
          <span className="text-sm font-semibold">{format(data?.likeCount)}</span>
        </Button>
        <Button className="gap-1 rounded-full" variant="ghost">
          <ChatTeardropIcon size={18} strokeWidth={1.6} />
          <span className="text-sm font-semibold">{format(data?.commentCount)}</span>
        </Button>
        <HoverCard>
          <HoverCardTrigger asChild>
            <Button className="rounded-full text-white" size="icon" variant="ghost">
              <InfoIcon size={18} strokeWidth={1.6} weight="bold" />
            </Button>
          </HoverCardTrigger>
          <HoverCardContent
            align="end"
            className="z-309 w-[376px] rounded-xl p-6"
            onWheel={e => {
              e.stopPropagation()
            }}
          >
            <WorkDetails />
          </HoverCardContent>
        </HoverCard>
        <div className="h-4">
          <Separator orientation="vertical" />
        </div>
        <ShareMenu link={shareLink} videoUrl={data?.videoUrl}>
          <Button className="gap-1 rounded-full" variant="ghost">
            <ShareFatIcon size={18} strokeWidth={1.6} weight="fill" />
            <span className="text-sm font-semibold">{t('common.share')}</span>
          </Button>
        </ShareMenu>
      </div>
    </div>
  )
}
